/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import ReactDOM from 'react-dom';
import Raven from 'raven-js';
import { BrowserRouter as Router } from 'react-router-dom';
import createStandardI18nProvider from 'I18n/init/providers/createStandardI18nProvider';
import registerAllData from 'I18n/init/register/lazy/registerAllData';
import { initAppTranslations } from 'I18n/init/setup/AppInit';
import lang from 'i2l?query=sporks!../lang/en.lyaml';
import langNav from 'i2l?query=sporks!../lang/modules/academy-nav/en.lyaml';
import langShared from 'i2l?query=sporks!../lang/modules/academy-shared/en.lyaml';

// Laboratory copy experiments
import createExperimentI18nProvider from 'laboratory-lib/i18n/createExperimentI18nProvider';
import experimentalLang from 'i2l?query=sporks!../lang/laboratory-copy-experiments/en.lyaml';
import { TreatmentsClient } from './features/experiments';
import SimpleHubPicker from 'hub-pickers-lib/components/SimpleHubPicker';
import PortalIdParser from 'PortalIdParser';
import LearningCenter from './LearningCenter';
import HublessError from './components/Errors/HublessError';
import ApiError from './components/Errors/ApiError';
import { addUrlParams, replacePortalIdInPath, getUnauthenticatedUserRedirect } from './utils/RoutingUtils';
import { isHubSpotNativeApp, getLanguageFromSignup } from './utils/utils';
import 'I18n/init/data/currency';
import { getUserInfo, getCrossAccounts, getGatesAndScopes } from './features/accounts';
import { parse, stringify } from 'hub-http/helpers/params';
import './features/export/exportSetup';
import { startFireAlarm } from './utils/FireAlarm';
import { jsx as _jsx } from "react/jsx-runtime";
export default class Application {
  start() {
    if (window.jasmine) {
      return;
    }
    if (isHubSpotNativeApp) {
      this.mobileAppStyleEl = document.createElement('style');
      this.mobileAppStyleEl.id = 'hs-nav-hide-style-mobile-app';
      this.mobileAppStyleEl.innerHTML = '#hs-nav-v4, #chat-widget { display: none; }';
      document.getElementsByTagName('head')[0].appendChild(this.mobileAppStyleEl);
    }
    const portalIdFromUrl = PortalIdParser.getPortalIdFromPath();
    window.hubspot.portal = {
      id: portalIdFromUrl
    };
    const StandardI18nProvider = createStandardI18nProvider();
    const ExperimentalI18nProvider = createExperimentI18nProvider({
      client: TreatmentsClient.get()
    });
    const I18nProviders = [[StandardI18nProvider, lang], [StandardI18nProvider, langNav], [StandardI18nProvider, langShared], [ExperimentalI18nProvider, experimentalLang]];
    const I18nRegistrations = I18nProviders.map(([provider, langFile]) => provider.register(langFile));
    const langAvailable = initAppTranslations([...I18nRegistrations, registerAllData(StandardI18nProvider)]);

    // Switch 'lang' param to 'language' -  ONLY if we do not already have a language param
    const switchLangParamForLanguage = () => {
      const params = parse(window.location.search.slice(1));
      if (!params['language']) {
        Object.keys(params).forEach(param => {
          if (param === 'lang' && params[param] !== 'undefined') {
            params['language'] = params[param].toUpperCase().split('-')[0];
            delete params[param];
            window.location.replace(`${window.location.pathname}?${stringify(params)}`);
          }
        });
      } else {
        // We only support base languages (EN, PT, ES) and not sublanguage codes (EN-US, PT-BR)
        const splitLanguage = params['language'].split('-');
        if (splitLanguage.length > 1) {
          params['language'] = splitLanguage[0].toUpperCase();
          window.location.replace(`${window.location.pathname}?${stringify(params)}`);
        }
      }
    };
    switchLangParamForLanguage();
    startFireAlarm();
    const init = Promise.all([getUserInfo(), getCrossAccounts(), getGatesAndScopes()]);
    const signupLang = getLanguageFromSignup();
    init.then(([{
      user,
      actingUserId
    }, {
      accounts
    }, {
      gates,
      scopes
    }]) => {
      this.redirectToImpersonationSplash(actingUserId);
      I18nProviders.forEach(([I18nProvider]) => {
        I18nProvider.setLocale({
          locale: signupLang || user.locale,
          langEnabled: signupLang ? true : user.languageEnabled
        });
      });
      Raven.setUserContext({
        id: user.user_id,
        email: user.email,
        locale: user.locale
      });
      langAvailable.then(() => {
        if (accounts.length === 1) {
          if (accounts[0].id === portalIdFromUrl) {
            this.startRouter(portalIdFromUrl, user, gates, scopes);
          } else {
            window.location.replace(replacePortalIdInPath({
              portalId: accounts[0].id,
              hublet: accounts[0].hublet
            }));
          }
        } else if (!accounts.length) {
          const {
            email,
            emails
          } = user;
          const userEmail = email || emails && emails[0];
          ReactDOM.render( /*#__PURE__*/_jsx(HublessError, {
            email: userEmail,
            firstName: user.firstName
          }), document.getElementsByClassName('page')[0]);
        } else if (!portalIdFromUrl || !accounts.find(account => account.id === portalIdFromUrl)) {
          if (portalIdFromUrl) {
            const firstAccountHublet = accounts[0] && accounts[0].hublet;
            window.location.replace(addUrlParams(replacePortalIdInPath({
              portalId: '',
              hublet: firstAccountHublet
            }), {
              rpid: portalIdFromUrl // 'Referrer portal id' for viral tracking
            }));
            return;
          } else {
            ReactDOM.render( /*#__PURE__*/_jsx("div", {
              "data-test-id": "simple-hub-picker",
              style: {
                maxWidth: 800,
                margin: '30px auto'
              },
              children: /*#__PURE__*/_jsx(SimpleHubPicker, {
                redirectTo: portalId => replacePortalIdInPath({
                  portalId,
                  hublet: accounts.find(account => account.id === portalId).hublet
                })
              })
            }), document.getElementsByClassName('page')[0]);
          }
        } else {
          this.startRouter(portalIdFromUrl, user, gates, scopes);
        }
      });
    }).catch((error = {}) => {
      console.error(error);
      if (error && error.status === 401) {
        window.location.href = getUnauthenticatedUserRedirect(window.location.href);
      } else {
        // Set locale + timezone data for each provider
        I18nProviders.forEach(([I18nProvider]) => {
          I18nProvider.setLocale({});
        });
        langAvailable.then(() => {
          ReactDOM.render( /*#__PURE__*/_jsx(Router, {
            basename: "academy",
            children: /*#__PURE__*/_jsx(ApiError, {
              error: {
                message: 'Error loading early requests',
                status: error && error.status,
                responseJSON: error && error.responseJSON
              }
            })
          }), document.getElementsByClassName('page')[0]);
        });
      }
    });
  }
  startRouter(portalId, user, gates, scopes) {
    const v3Nav = document.getElementById('hs-nav-v3');
    if (v3Nav) {
      v3Nav.classList.add('no-bottom-margin');
    }
    ReactDOM.render( /*#__PURE__*/_jsx(LearningCenter, {
      gates: gates,
      portalId: portalId,
      scopes: scopes,
      user: user
    }), document.getElementsByClassName('page')[0]);
  }
  redirectToImpersonationSplash(actingUserId) {
    if (actingUserId) {
      window.location = '/login-as-error';
    }
  }
}